import { Formik, Form, FormikProps } from 'formik';

import * as Yup from 'yup';
import cn from 'classnames';
import { tryLogin } from 'api/auth';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import styles from './LoginForm.module.scss';

interface IFormProps {
  email: string;
  password: string;
}

export const LoginForm = () => {
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState('');
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const { t } = useTranslation();
  const LogInSchema = Yup.object().shape({
    email: Yup.string()
      .min(2, `${t('email min')} 2`)
      .email(`${t('email invalid')}`)
      .required(`${t('email required')}`),
    password: Yup.string()
      .min(2, `${t('password min')} 2`)
      .required(`${t('password required')}`)
  });

  const handleFormSubmit = async (values: IFormProps) => {
    const data = {
      email: values.email,
      password: values.password
    };

    try {
      setIsDisabledButton(true);
      const response = await tryLogin(data);
      const status = response.status;
      const tokens = JSON.stringify(response.data?.data);

      if (status === 200) {
        localStorage.setItem('authTokens', tokens);
        navigate('/dashboard');
      }
    } catch (error) {
      const err = error as AxiosError;

      if (err.response) {
        const errorMsg = err.response.data.errors[0].message;
        setLoginError(errorMsg);
      }
    } finally {
      setIsDisabledButton(false);
    }
  };

  const initialValues: IFormProps = {
    email: '',
    password: ''
  };

  return (
    <div className={styles.root}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={LogInSchema}
      >
        {({ values, errors, handleChange, touched }: FormikProps<IFormProps>) => (
          <Form className={styles.form}>
            <div className={styles.group}>
              {errors.email && touched.email && (
                <div className={styles.errorTxt}>{errors.email}</div>
              )}
              {loginError && !errors.email && touched.email && (
                <div className={styles.errorTxt}>{loginError}</div>
              )}
              <input
                type="text"
                value={values.email}
                onChange={handleChange('email')}
                onInput={() => {
                  setLoginError('');
                }}
                name="email"
                id="email"
                placeholder={t('email')}
                className={cn(
                  styles.input,
                  (errors.email && touched.email) || loginError ? styles.error : ''
                )}
              />
            </div>
            <div className={styles.group}>
              {errors.password && touched.password && (
                <div className={styles.errorTxt}>{errors.password}</div>
              )}
              <input
                type="password"
                value={values.password}
                onChange={handleChange('password')}
                onInput={() => {
                  setLoginError('');
                }}
                name="password"
                id="password"
                placeholder={t('password')}
                className={cn(
                  styles.input,
                  (errors.password && touched.password) || loginError ? styles.error : ''
                )}
              />
            </div>
            <button
              type="submit"
              className={styles.btn}
              disabled={
                !!(errors.email && touched.email) ||
                !!(errors.password && touched.password) ||
                isDisabledButton
              }
            >
              {t('submit')}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};
